import { AxiosInstance } from "axios";

import { getClient } from "../axios";

import {
    BaloiseGetSimulationTariffsRequest,
    BaloisePostQuoteRequest,
    BaloisePostWeTakeCareLeadRequest,
    BusinessRulesDTO,
    FordGetQuoteTariffsRequest,
    FordGetSimulationTariffsRequest,
    FordPostLeadRequest,
    FordPostQuoteRequest,
    QuoteDto,
    QuoteTariffsDTO,
    SimulationDto,
} from "./mobility.types";

/**
 * Service class for managing mobility related operations.
 */
class MobilityService {
    /**
     * HTTP client instance for making requests.
     */
    public client: AxiosInstance;

    /**
     * Base path for address-related API endpoints.
     */
    private basePath = "mob";

    constructor() {
        this.client = getClient();
    }

    /**
     * Constructs the full API endpoint path.
     * @param path - The specific endpoint path to append to the base path.
     * @returns The full API endpoint path.
     */
    private getFullPath(path = ""): string {
        return `${this.basePath}${path}`;
    }

    public getBusinessRules() {
        return this.client
            .get<BusinessRulesDTO>(this.getFullPath("/businessrules"))
            .then((result) => result.data);
    }

    public baloiseGetSimulationTariffs(
        baloiseGetSimulationTariffsRequest: BaloiseGetSimulationTariffsRequest,
    ) {
        return this.client
            .post<SimulationDto>(
                this.getFullPath("/tariffs/baloise/simulation"),
                baloiseGetSimulationTariffsRequest,
            )
            .then((result) => result.data);
    }

    public baloisePostQuote(baloisePostQuoteRequest: BaloisePostQuoteRequest) {
        return this.client
            .post<QuoteDto>(
                this.getFullPath("/quotes/baloise"),
                baloisePostQuoteRequest,
            )
            .then((result) => result.data);
    }

    public baloisePostWeTakeCareLead(
        baloisePostWeTakeCareLeadRequest: BaloisePostWeTakeCareLeadRequest,
    ) {
        return this.client
            .post<QuoteDto>(
                this.getFullPath("/leads/baloise/wtc"),
                baloisePostWeTakeCareLeadRequest,
            )
            .then((result) => result.data);
    }

    public fordGetQuoteTariffs(
        consumerApplication: string,
        fordGetQuoteTariffsRequest: FordGetQuoteTariffsRequest,
    ) {
        return this.client
            .post<QuoteTariffsDTO>(
                this.getFullPath("/tariffs/ford/quote"),
                fordGetQuoteTariffsRequest,
                {
                    headers: {
                        ConsumerApplication: consumerApplication,
                    },
                },
            )
            .then((result) => result.data);
    }

    public fordPostSimulationLead(
        consumerApplication: string,
        fordPostLeadRequest: FordPostLeadRequest,
    ) {
        return this.client
            .post<void>(
                this.getFullPath("/leads/ford/simulation"),
                fordPostLeadRequest,
                {
                    headers: {
                        ConsumerApplication: consumerApplication,
                    },
                },
            )
            .then((result) => result.data);
    }

    public fordPostDealerLead(
        consumerApplication: string,
        fordPostLeadRequest: FordPostLeadRequest,
    ) {
        return this.client
            .post<void>(
                this.getFullPath("/leads/ford/dealer"),
                fordPostLeadRequest,
                {
                    headers: {
                        ConsumerApplication: consumerApplication,
                    },
                },
            )
            .then((result) => result.data);
    }

    public fordGetSimulationTariffs(
        consumerApplication: string,
        fordGetSimulationTariffsRequest: FordGetSimulationTariffsRequest,
    ) {
        return this.client
            .post<SimulationDto>(
                this.getFullPath("/tariffs/ford/simulation"),
                fordGetSimulationTariffsRequest,
                {
                    headers: {
                        ConsumerApplication: consumerApplication,
                    },
                },
            )
            .then((result) => result.data);
    }

    public fordPostQuote(
        consumerApplication: string,
        fordPostQuoteRequest: FordPostQuoteRequest,
    ) {
        return this.client
            .post<void>(
                this.getFullPath("/quotes/ford"),
                fordPostQuoteRequest,
                {
                    headers: {
                        ConsumerApplication: consumerApplication,
                    },
                },
            )
            .then((result) => result.data);
    }
}

export const mobilityService = new MobilityService();
